<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';

import VtexAPI from 'dashboard/api/integrations/vtex';
import Integration from './Integration.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const connectDialogRef = ref(null);
const formError = ref('');
const searchError = ref('');
const searchQuery = ref('');
const searchResults = ref([]);
const inboxSearchQuery = ref('');
const isSavingModules = ref(false);
const isSearchingProducts = ref(false);
const isSyncingCatalog = ref(false);
const expandedInboxIds = ref({});
const catalogSyncPollTimer = ref(null);

const form = ref({
  account_name: '',
  environment: 'vtexcommercestable',
  app_key: '',
  app_token: '',
  modules: {
    customer_lookup: true,
    orders_sidebar: true,
    tracking_sidebar: true,
    payments_sidebar: true,
    returns_sidebar: true,
    catalog_search: true,
    price_lookup: true,
    inventory_lookup: true,
    draft_orders: true,
    checkout_approval: true,
  },
  inbox_overrides: {},
  catalog_exclusions: {
    keywords: [],
    category_paths: [],
    brands: [],
    product_ids: [],
  },
});
const bulkInboxOverrideForm = ref({
  enabled: true,
  checkout_domain: '',
  sales_channel: '',
  default_seller: '',
  modules: {},
});

const integration = useFunctionGetter('integrations/getIntegration', 'vtex');
const uiFlags = useMapGetter('integrations/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

const hook = computed(() => integration.value?.hooks?.[0] || null);
const settings = computed(() => hook.value?.settings || {});
const capabilities = computed(() => {
  const rawCapabilities = settings.value.capabilities || {};
  const customerLookup = rawCapabilities.customer_lookup === true;
  const ordersSidebar = rawCapabilities.orders_sidebar === true;
  const catalogSearch = rawCapabilities.catalog_search === true;

  return {
    customer_lookup: customerLookup,
    orders_sidebar: ordersSidebar,
    tracking_sidebar: rawCapabilities.tracking_sidebar ?? ordersSidebar,
    payments_sidebar: rawCapabilities.payments_sidebar ?? ordersSidebar,
    returns_sidebar: rawCapabilities.returns_sidebar ?? ordersSidebar,
    catalog_search: catalogSearch,
    price_lookup: rawCapabilities.price_lookup ?? catalogSearch,
    inventory_lookup: rawCapabilities.inventory_lookup ?? catalogSearch,
    draft_orders:
      rawCapabilities.draft_orders ?? (customerLookup && catalogSearch),
    checkout_approval:
      rawCapabilities.checkout_approval ?? (customerLookup && catalogSearch),
  };
});
const moduleItems = computed(() => [
  {
    key: 'customer_lookup',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CUSTOMER_LOOKUP.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CUSTOMER_LOOKUP.DESCRIPTION'
    ),
  },
  {
    key: 'orders_sidebar',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.ORDERS_SIDEBAR.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.ORDERS_SIDEBAR.DESCRIPTION'
    ),
  },
  {
    key: 'tracking_sidebar',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.TRACKING_SIDEBAR.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.TRACKING_SIDEBAR.DESCRIPTION'
    ),
  },
  {
    key: 'payments_sidebar',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.PAYMENTS_SIDEBAR.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.PAYMENTS_SIDEBAR.DESCRIPTION'
    ),
  },
  {
    key: 'returns_sidebar',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.RETURNS_SIDEBAR.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.RETURNS_SIDEBAR.DESCRIPTION'
    ),
  },
  {
    key: 'catalog_search',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CATALOG_SEARCH.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CATALOG_SEARCH.DESCRIPTION'
    ),
  },
  {
    key: 'price_lookup',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.PRICE_LOOKUP.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.PRICE_LOOKUP.DESCRIPTION'
    ),
  },
  {
    key: 'inventory_lookup',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.INVENTORY_LOOKUP.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.INVENTORY_LOOKUP.DESCRIPTION'
    ),
  },
  {
    key: 'draft_orders',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.DRAFT_ORDERS.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.DRAFT_ORDERS.DESCRIPTION'
    ),
  },
  {
    key: 'checkout_approval',
    title: t('INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CHECKOUT_APPROVAL.TITLE'),
    description: t(
      'INTEGRATION_SETTINGS.VTEX.MODULES.ITEMS.CHECKOUT_APPROVAL.DESCRIPTION'
    ),
  },
]);
const capabilityItems = computed(() => [
  {
    key: 'customer_lookup',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.CUSTOMER_LOOKUP'),
  },
  {
    key: 'orders_sidebar',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.ORDERS_SIDEBAR'),
  },
  {
    key: 'tracking_sidebar',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.TRACKING_SIDEBAR'),
  },
  {
    key: 'payments_sidebar',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.PAYMENTS_SIDEBAR'),
  },
  {
    key: 'returns_sidebar',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.RETURNS_SIDEBAR'),
  },
  {
    key: 'catalog_search',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.CATALOG_SEARCH'),
  },
  {
    key: 'price_lookup',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.PRICE_LOOKUP'),
  },
  {
    key: 'inventory_lookup',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.INVENTORY_LOOKUP'),
  },
  {
    key: 'draft_orders',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.DRAFT_ORDERS'),
  },
  {
    key: 'checkout_approval',
    label: t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ITEMS.CHECKOUT_APPROVAL'),
  },
]);
const inboxOverrides = computed(() => form.value.inbox_overrides || {});
const catalogExclusions = computed(() => form.value.catalog_exclusions || {});
const catalogSync = computed(() => settings.value.catalog_sync || {});
const catalogSyncStats = computed(() => catalogSync.value.counters || {});
const catalogSyncReadyCount = computed(
  () => catalogSyncStats.value.upserted || 0
);
const shouldPollCatalogSync = computed(() =>
  ['queued', 'running', 'syncing'].includes(catalogSync.value.status || '')
);
const inboxItems = computed(() =>
  (inboxes.value || []).map(inbox => {
    const override = inboxOverrides.value?.[`${inbox.id}`] || {};
    return {
      id: inbox.id,
      name: inbox.name,
      override,
    };
  })
);
const filteredInboxItems = computed(() => {
  const normalizedQuery = inboxSearchQuery.value.trim().toLowerCase();
  if (!normalizedQuery) {
    return inboxItems.value;
  }

  return inboxItems.value.filter(inbox =>
    inbox.name.toLowerCase().includes(normalizedQuery)
  );
});
const exclusionFields = computed(() => [
  {
    key: 'keywords',
    label: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.KEYWORDS_LABEL'),
    placeholder: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.KEYWORDS_PLACEHOLDER'
    ),
    helper: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.KEYWORDS_HELP'),
  },
  {
    key: 'category_paths',
    label: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.CATEGORY_PATHS_LABEL'
    ),
    placeholder: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.CATEGORY_PATHS_PLACEHOLDER'
    ),
    helper: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.CATEGORY_PATHS_HELP'
    ),
  },
  {
    key: 'brands',
    label: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.BRANDS_LABEL'),
    placeholder: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.BRANDS_PLACEHOLDER'
    ),
    helper: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.BRANDS_HELP'),
  },
  {
    key: 'product_ids',
    label: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.PRODUCT_IDS_LABEL'),
    placeholder: t(
      'INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.PRODUCT_IDS_PLACEHOLDER'
    ),
    helper: t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.PRODUCT_IDS_HELP'),
  },
]);
const catalogSyncStatusLabel = computed(() => {
  const status = catalogSync.value.status || 'idle';
  const labels = {
    disabled: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.DISABLED'),
    idle: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.IDLE'),
    queued: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.QUEUED'),
    running: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.SYNCING'),
    syncing: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.SYNCING'),
    success: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.SYNCED'),
    synced: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.SYNCED'),
    failed: t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STATUS.FAILED'),
  };

  return labels[status] || status;
});
const catalogSyncStatusClass = computed(() => {
  const status = catalogSync.value.status || 'idle';
  const classes = {
    disabled: 'bg-n-alpha-black2 text-n-slate-11',
    success: 'bg-n-teal-2 text-n-teal-11',
    synced: 'bg-n-teal-2 text-n-teal-11',
    running: 'bg-n-brand/10 text-n-brand',
    syncing: 'bg-n-brand/10 text-n-brand',
    queued: 'bg-n-amber-2 text-n-amber-11',
    failed: 'bg-n-ruby-2 text-n-ruby-11',
    idle: 'bg-n-alpha-black2 text-n-slate-11',
  };

  return classes[status] || classes.idle;
});
const isCatalogSyncDisabled = computed(
  () => (catalogSync.value.status || 'idle') === 'disabled'
);

const buildBulkModules = () =>
  moduleItems.value.reduce((accumulator, module) => {
    accumulator[module.key] = form.value.modules[module.key] ?? true;
    return accumulator;
  }, {});

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }

  return 'connect';
});

const openConnectDialog = () => {
  formError.value = '';
  connectDialogRef.value?.open();
};

const closeConnectDialog = () => {
  formError.value = '';
  connectDialogRef.value?.close();
};

const onConnectDialogClose = () => {
  formError.value = '';
};

const ensureInboxOverride = inboxId => {
  if (form.value.inbox_overrides[`${inboxId}`]) {
    return form.value.inbox_overrides[`${inboxId}`];
  }

  form.value.inbox_overrides[`${inboxId}`] = {
    enabled: true,
    checkout_domain: '',
    sales_channel: '',
    default_seller: '',
    modules: {},
  };

  return form.value.inbox_overrides[`${inboxId}`];
};

const initializeBulkInboxOverrideForm = () => {
  bulkInboxOverrideForm.value = {
    enabled: true,
    checkout_domain: '',
    sales_channel: '',
    default_seller: '',
    modules: buildBulkModules(),
  };
};

const isInboxExpanded = inboxId => expandedInboxIds.value[inboxId] !== false;

const setInboxExpanded = (inboxId, expanded) => {
  expandedInboxIds.value = {
    ...expandedInboxIds.value,
    [inboxId]: expanded,
  };
};

const toggleInboxExpanded = inboxId => {
  setInboxExpanded(inboxId, !isInboxExpanded(inboxId));
};

const expandAllInboxes = () => {
  expandedInboxIds.value = filteredInboxItems.value.reduce(
    (accumulator, inbox) => {
      accumulator[inbox.id] = true;
      return accumulator;
    },
    {}
  );
};

const collapseAllInboxes = () => {
  expandedInboxIds.value = filteredInboxItems.value.reduce(
    (accumulator, inbox) => {
      accumulator[inbox.id] = false;
      return accumulator;
    },
    {}
  );
};

const applyBulkInboxStatus = enabled => {
  filteredInboxItems.value.forEach(inbox => {
    ensureInboxOverride(inbox.id).enabled = enabled;
  });
};

const applyBulkCheckoutSettings = () => {
  filteredInboxItems.value.forEach(inbox => {
    const override = ensureInboxOverride(inbox.id);
    override.enabled = bulkInboxOverrideForm.value.enabled;

    if (bulkInboxOverrideForm.value.checkout_domain.trim()) {
      override.checkout_domain =
        bulkInboxOverrideForm.value.checkout_domain.trim();
    }

    if (bulkInboxOverrideForm.value.sales_channel.trim()) {
      override.sales_channel = bulkInboxOverrideForm.value.sales_channel.trim();
    }

    if (bulkInboxOverrideForm.value.default_seller.trim()) {
      override.default_seller =
        bulkInboxOverrideForm.value.default_seller.trim();
    }
  });
};

const applyBulkModules = () => {
  filteredInboxItems.value.forEach(inbox => {
    const override = ensureInboxOverride(inbox.id);
    override.modules = {
      ...(override.modules || {}),
      ...bulkInboxOverrideForm.value.modules,
    };
  });
};

const normalizeCatalogExclusions = exclusions => ({
  keywords: Array(exclusions?.keywords),
  category_paths: Array(exclusions?.category_paths),
  brands: Array(exclusions?.brands),
  product_ids: Array(exclusions?.product_ids),
});

const syncFormFromHook = () => {
  form.value.account_name = settings.value.connection?.account_name || '';
  form.value.environment = settings.value.environment || 'vtexcommercestable';
  form.value.modules = {
    customer_lookup: settings.value.modules?.customer_lookup ?? true,
    orders_sidebar: settings.value.modules?.orders_sidebar ?? true,
    tracking_sidebar: settings.value.modules?.tracking_sidebar ?? true,
    payments_sidebar: settings.value.modules?.payments_sidebar ?? true,
    returns_sidebar: settings.value.modules?.returns_sidebar ?? true,
    catalog_search: settings.value.modules?.catalog_search ?? true,
    price_lookup: settings.value.modules?.price_lookup ?? true,
    inventory_lookup: settings.value.modules?.inventory_lookup ?? true,
    draft_orders: settings.value.modules?.draft_orders ?? true,
    checkout_approval: settings.value.modules?.checkout_approval ?? true,
  };
  form.value.inbox_overrides = settings.value.inbox_overrides || {};
  form.value.catalog_exclusions = normalizeCatalogExclusions(
    settings.value.catalog_exclusions
  );
  initializeBulkInboxOverrideForm();
};

const parseCatalogExclusionValue = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

const catalogExclusionValue = key =>
  Array(catalogExclusions.value[key] || []).join('\n');

const updateCatalogExclusionValue = (key, value) => {
  form.value.catalog_exclusions = {
    ...catalogExclusions.value,
    [key]: parseCatalogExclusionValue(value),
  };
};

const formatDateTime = value => {
  if (!value) {
    return t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.NEVER');
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const refreshVtexIntegration = async () => {
  await store.dispatch('integrations/get');
  syncFormFromHook();
};

const stopCatalogSyncPolling = () => {
  if (!catalogSyncPollTimer.value) {
    return;
  }

  clearInterval(catalogSyncPollTimer.value);
  catalogSyncPollTimer.value = null;
};

const startCatalogSyncPolling = () => {
  if (catalogSyncPollTimer.value) {
    return;
  }

  catalogSyncPollTimer.value = setInterval(() => {
    refreshVtexIntegration();
  }, 8000);
};

const initializeVtexIntegration = async () => {
  await Promise.all([
    store.dispatch('integrations/get'),
    store.dispatch('inboxes/get'),
  ]);
  syncFormFromHook();
  integrationLoaded.value = true;
};

const connectVtex = async () => {
  formError.value = '';

  if (
    !form.value.account_name ||
    !form.value.app_key ||
    !form.value.app_token
  ) {
    formError.value = t('INTEGRATION_SETTINGS.VTEX.FORM.REQUIRED_ERROR');
    return;
  }

  try {
    await store.dispatch('integrations/connectVtex', {
      account_name: form.value.account_name,
      environment: form.value.environment,
      app_key: form.value.app_key,
      app_token: form.value.app_token,
    });
    form.value.app_key = '';
    form.value.app_token = '';
    await store.dispatch('integrations/get');
    syncFormFromHook();
    closeConnectDialog();
  } catch (error) {
    formError.value = error.message;
  }
};

const saveModules = async () => {
  if (isSavingModules.value) {
    return;
  }

  formError.value = '';
  isSavingModules.value = true;

  try {
    await store.dispatch('integrations/updateVtexConfig', {
      modules: form.value.modules,
      inbox_overrides: form.value.inbox_overrides,
      catalog_exclusions: form.value.catalog_exclusions,
    });
    await store.dispatch('integrations/get');
    syncFormFromHook();
  } catch (error) {
    formError.value = error.message;
  } finally {
    isSavingModules.value = false;
  }
};

const triggerCatalogSync = async () => {
  if (isSyncingCatalog.value) {
    return;
  }

  formError.value = '';
  isSyncingCatalog.value = true;

  try {
    await VtexAPI.triggerCatalogSync();
    await refreshVtexIntegration();
  } catch (error) {
    formError.value = error.response?.data?.error || error.message;
  } finally {
    isSyncingCatalog.value = false;
  }
};

const searchProducts = async () => {
  if (isSearchingProducts.value) {
    return;
  }

  searchError.value = '';

  if (!searchQuery.value.trim()) {
    searchError.value = t('INTEGRATION_SETTINGS.VTEX.SEARCH.EMPTY_ERROR');
    return;
  }

  isSearchingProducts.value = true;

  try {
    const response = await VtexAPI.searchProducts(searchQuery.value.trim());
    searchResults.value = response.data.products || [];
  } catch (error) {
    searchError.value = error.response?.data?.error || error.message;
  } finally {
    isSearchingProducts.value = false;
  }
};

const formatProductPrice = product => {
  if (!product.price) {
    return t('INTEGRATION_SETTINGS.VTEX.SEARCH.NO_PRICE');
  }

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: product.currency || 'USD',
  }).format(product.price);
};

const stockLabel = product => {
  if (!capabilities.value.inventory_lookup) {
    return t('INTEGRATION_SETTINGS.VTEX.SEARCH.STOCK_UNKNOWN');
  }

  const rawQuantity =
    product.stock_quantity ??
    product.availability ??
    product.available_quantity;

  if (rawQuantity === null || rawQuantity === undefined || rawQuantity === '') {
    return t('INTEGRATION_SETTINGS.VTEX.SEARCH.STOCK_UNKNOWN');
  }

  const availableQuantity = Number(rawQuantity || 0);
  return product.in_stock !== false && availableQuantity > 0
    ? t('INTEGRATION_SETTINGS.VTEX.SEARCH.IN_STOCK', {
        count: availableQuantity,
      })
    : t('INTEGRATION_SETTINGS.VTEX.SEARCH.OUT_OF_STOCK');
};

const productAttributes = product => {
  return (product.variations || []).filter(
    variation => variation.name && variation.value
  );
};

const productGallery = product => {
  return product.images?.length
    ? product.images
    : [product.primary_image].filter(Boolean);
};

const formatLabelValue = (label, value) =>
  t('INTEGRATION_SETTINGS.VTEX.SEARCH.LABEL_VALUE', {
    label,
    value,
  });

onMounted(() => {
  initializeVtexIntegration();
});

watch(shouldPollCatalogSync, isPolling => {
  if (isPolling) {
    startCatalogSyncPolling();
  } else {
    stopCatalogSyncPolling();
  }
});

onUnmounted(() => {
  stopCatalogSyncPolling();
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded || uiFlags.isCreatingVtex">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.VTEX.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.VTEX.DESCRIPTION')"
        feature-name="vtex_integration"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id || 'vtex'"
          :integration-logo="integration.logo"
          :integration-name="
            integration.name || $t('INTEGRATION_SETTINGS.VTEX.HEADER')
          "
          :integration-description="
            integration.description ||
            $t('INTEGRATION_SETTINGS.VTEX.DESCRIPTION')
          "
          :integration-enabled="integration.enabled || false"
          :integration-action="integrationAction"
          :delete-confirmation-text="{
            title: t('INTEGRATION_SETTINGS.VTEX.DELETE.TITLE'),
            message: t('INTEGRATION_SETTINGS.VTEX.DELETE.MESSAGE'),
          }"
        >
          <template #action>
            <Button
              v-if="!integration.enabled"
              faded
              blue
              type="button"
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openConnectDialog"
            />
          </template>
        </Integration>

        <div
          v-if="hook"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VTEX.MODULES.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INTEGRATION_SETTINGS.VTEX.MODULES.DESCRIPTION') }}
            </p>
          </div>

          <div class="flex flex-col gap-3">
            <div
              v-for="module in moduleItems"
              :key="module.key"
              class="rounded-lg border border-n-weak p-4 flex items-center justify-between gap-4"
            >
              <div>
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{ module.title }}
                </h4>
                <p class="text-sm text-n-slate-11 mt-1">
                  {{ module.description }}
                </p>
                <p
                  class="text-xs mt-1"
                  :class="
                    capabilities[module.key]
                      ? 'text-n-teal-10'
                      : 'text-n-amber-10'
                  "
                >
                  {{
                    capabilities[module.key]
                      ? $t('INTEGRATION_SETTINGS.VTEX.MODULES.AVAILABLE')
                      : $t('INTEGRATION_SETTINGS.VTEX.MODULES.UNAVAILABLE')
                  }}
                </p>
              </div>
              <Switch v-model="form.modules[module.key]" />
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              type="button"
              :label="$t('INTEGRATION_SETTINGS.VTEX.MODULES.SAVE')"
              :is-loading="uiFlags.isUpdatingVtex || isSavingModules"
              @click="saveModules"
            />
          </div>
        </div>

        <div
          v-if="hook"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-5"
        >
          <div
            class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
          >
            <div class="max-w-2xl">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.DESCRIPTION') }}
              </p>
              <p
                v-if="isCatalogSyncDisabled"
                class="mt-2 text-sm text-n-slate-11"
              >
                {{
                  $t(
                    'INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.DISABLED_DESCRIPTION'
                  )
                }}
              </p>
              <p v-if="shouldPollCatalogSync" class="mt-2 text-sm text-n-brand">
                {{
                  $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.RUNNING_HINT', {
                    count: catalogSyncReadyCount,
                  })
                }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <span
                class="rounded-full px-3 py-1 text-xs font-medium"
                :class="catalogSyncStatusClass"
              >
                {{ catalogSyncStatusLabel }}
              </span>
              <Button
                type="button"
                slate
                :label="$t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.ACTION')"
                :is-loading="isSyncingCatalog"
                :disabled="isCatalogSyncDisabled"
                @click="triggerCatalogSync"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.READY_NOW') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ catalogSyncReadyCount }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.STARTED_AT') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ formatDateTime(catalogSync.started_at) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.LAST_BATCH_AT') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ formatDateTime(catalogSync.last_batch_at) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{
                  $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.PROCESSED_BATCHES')
                }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ catalogSyncStats.processed_batches || 0 }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.FETCHED') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ catalogSyncStats.fetched || 0 }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.UPSERTED') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ catalogSyncStats.upserted || 0 }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-4">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.EXCLUDED') }}
              </p>
              <p class="mt-2 text-sm text-n-slate-12">
                {{ catalogSyncStats.excluded || 0 }}
              </p>
            </div>
          </div>

          <div
            v-if="catalogSync.last_error"
            class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11"
          >
            <span class="font-medium">
              {{
                `${$t('INTEGRATION_SETTINGS.VTEX.CATALOG_SYNC.LAST_ERROR')}:`
              }}
            </span>
            {{ catalogSync.last_error }}
          </div>
        </div>

        <div
          v-if="hook"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                $t('INTEGRATION_SETTINGS.VTEX.CATALOG_EXCLUSIONS.DESCRIPTION')
              }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div
              v-for="field in exclusionFields"
              :key="field.key"
              class="rounded-xl border border-n-weak p-4"
            >
              <TextArea
                :model-value="catalogExclusionValue(field.key)"
                :label="field.label"
                :placeholder="field.placeholder"
                :message="field.helper"
                auto-height
                :max-length="4000"
                show-character-count
                @update:model-value="
                  value => updateCatalogExclusionValue(field.key, value)
                "
              />
            </div>
          </div>

          <div v-if="formError" class="text-sm text-n-ruby-10">
            {{ formError }}
          </div>

          <div class="flex justify-end">
            <Button
              type="button"
              :label="$t('INTEGRATION_SETTINGS.VTEX.MODULES.SAVE')"
              :is-loading="uiFlags.isUpdatingVtex || isSavingModules"
              @click="saveModules"
            />
          </div>
        </div>

        <div
          v-if="hook"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.DESCRIPTION') }}
            </p>
          </div>

          <div class="rounded-xl border border-n-weak p-4 flex flex-col gap-4">
            <div
              class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between"
            >
              <div class="flex-1">
                <Input
                  v-model="inboxSearchQuery"
                  :label="
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.SEARCH_LABEL')
                  "
                  :placeholder="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.SEARCH_PLACEHOLDER'
                    )
                  "
                />
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.EXPAND_ALL')
                  "
                  @click="expandAllInboxes"
                />
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.COLLAPSE_ALL')
                  "
                  @click="collapseAllInboxes"
                />
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.ENABLE_ALL')
                  "
                  @click="applyBulkInboxStatus(true)"
                />
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.DISABLE_ALL')
                  "
                  @click="applyBulkInboxStatus(false)"
                />
              </div>
            </div>

            <div
              class="rounded-lg border border-n-weak p-4 flex flex-col gap-4"
            >
              <div>
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.BULK_TITLE')
                  }}
                </h4>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.BULK_DESCRIPTION'
                    )
                  }}
                </p>
              </div>

              <div class="flex items-center justify-between gap-4">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.BULK_ENABLE'
                      )
                    }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.BULK_ENABLE_HINT'
                      )
                    }}
                  </p>
                </div>
                <Switch v-model="bulkInboxOverrideForm.enabled" />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  v-model="bulkInboxOverrideForm.checkout_domain"
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.CHECKOUT_DOMAIN'
                    )
                  "
                />
                <Input
                  v-model="bulkInboxOverrideForm.sales_channel"
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.SALES_CHANNEL'
                    )
                  "
                />
                <Input
                  v-model="bulkInboxOverrideForm.default_seller"
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.DEFAULT_SELLER'
                    )
                  "
                />
              </div>

              <div class="flex justify-end">
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.APPLY_SETTINGS'
                    )
                  "
                  @click="applyBulkCheckoutSettings"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="module in moduleItems"
                  :key="`bulk-${module.key}`"
                  class="rounded-lg border border-n-weak p-3 flex items-center justify-between gap-3"
                >
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{ module.title }}
                    </p>
                  </div>
                  <Switch v-model="bulkInboxOverrideForm.modules[module.key]" />
                </div>
              </div>

              <div class="flex justify-end">
                <Button
                  type="button"
                  slate
                  sm
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.APPLY_MODULES'
                    )
                  "
                  @click="applyBulkModules"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <div
              v-for="inbox in filteredInboxItems"
              :key="inbox.id"
              class="rounded-xl border border-n-weak p-4 flex flex-col gap-4"
            >
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ inbox.name }}
                  </h4>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{
                      $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.INBOX_HINT')
                    }}
                  </p>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <span
                      class="rounded-full px-2.5 py-1 text-xs font-medium"
                      :class="
                        ensureInboxOverride(inbox.id).enabled
                          ? 'bg-n-teal-2 text-n-teal-11'
                          : 'bg-n-alpha-black2 text-n-slate-11'
                      "
                    >
                      {{
                        ensureInboxOverride(inbox.id).enabled
                          ? $t(
                              'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.STATUS_ENABLED'
                            )
                          : $t(
                              'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.STATUS_DISABLED'
                            )
                      }}
                    </span>
                    <span
                      v-if="ensureInboxOverride(inbox.id).sales_channel"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        `${$t(
                          'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.SALES_CHANNEL'
                        )}: ${ensureInboxOverride(inbox.id).sales_channel}`
                      }}
                    </span>
                    <span
                      v-if="ensureInboxOverride(inbox.id).default_seller"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        `${$t(
                          'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.DEFAULT_SELLER'
                        )}: ${ensureInboxOverride(inbox.id).default_seller}`
                      }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-3 shrink-0">
                  <Switch v-model="ensureInboxOverride(inbox.id).enabled" />
                  <Button
                    type="button"
                    slate
                    sm
                    :label="
                      isInboxExpanded(inbox.id)
                        ? $t(
                            'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.COLLAPSE'
                          )
                        : $t('INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.EXPAND')
                    "
                    @click="toggleInboxExpanded(inbox.id)"
                  />
                </div>
              </div>

              <div v-if="isInboxExpanded(inbox.id)" class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    v-model="ensureInboxOverride(inbox.id).checkout_domain"
                    :label="
                      $t(
                        'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.CHECKOUT_DOMAIN'
                      )
                    "
                  />
                  <Input
                    v-model="ensureInboxOverride(inbox.id).sales_channel"
                    :label="
                      $t(
                        'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.SALES_CHANNEL'
                      )
                    "
                  />
                  <Input
                    v-model="ensureInboxOverride(inbox.id).default_seller"
                    :label="
                      $t(
                        'INTEGRATION_SETTINGS.VTEX.INBOX_OVERRIDES.DEFAULT_SELLER'
                      )
                    "
                  />
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    v-for="module in moduleItems"
                    :key="`${inbox.id}-${module.key}`"
                    class="rounded-lg border border-n-weak p-3 flex items-center justify-between gap-3"
                  >
                    <div>
                      <p class="text-sm font-medium text-n-slate-12">
                        {{ module.title }}
                      </p>
                    </div>
                    <Switch
                      v-model="
                        ensureInboxOverride(inbox.id).modules[module.key]
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              type="button"
              :label="$t('INTEGRATION_SETTINGS.VTEX.MODULES.SAVE')"
              :is-loading="uiFlags.isUpdatingVtex || isSavingModules"
              @click="saveModules"
            />
          </div>
        </div>

        <div
          v-if="hook"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.DESCRIPTION') }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div
              v-for="capability in capabilityItems"
              :key="capability.key"
              class="rounded-lg border border-n-weak p-4"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ capability.label }}
                </span>
                <span
                  class="rounded-full px-2.5 py-1 text-xs font-medium"
                  :class="
                    capabilities[capability.key]
                      ? 'bg-n-teal-2 text-n-teal-11'
                      : 'bg-n-amber-2 text-n-amber-11'
                  "
                >
                  {{
                    capabilities[capability.key]
                      ? $t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.ENABLED')
                      : $t('INTEGRATION_SETTINGS.VTEX.CAPABILITIES.DISABLED')
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="hook && form.modules.catalog_search"
          class="rounded-xl border border-n-weak bg-n-card p-5 flex flex-col gap-4"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VTEX.SEARCH.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INTEGRATION_SETTINGS.VTEX.SEARCH.DESCRIPTION') }}
            </p>
          </div>

          <div class="flex flex-col md:flex-row gap-3">
            <div class="flex-1">
              <Input
                v-model="searchQuery"
                :label="$t('INTEGRATION_SETTINGS.VTEX.SEARCH.INPUT_LABEL')"
                :placeholder="
                  $t('INTEGRATION_SETTINGS.VTEX.SEARCH.PLACEHOLDER')
                "
                @enter="searchProducts"
              />
            </div>
            <div class="flex items-end">
              <Button
                type="button"
                :label="$t('INTEGRATION_SETTINGS.VTEX.SEARCH.BUTTON')"
                :is-loading="isSearchingProducts"
                @click="searchProducts"
              />
            </div>
          </div>

          <div v-if="searchError" class="text-sm text-n-ruby-10">
            {{ searchError }}
          </div>

          <div
            v-if="!capabilities.catalog_search"
            class="text-sm text-n-amber-10"
          >
            {{ $t('INTEGRATION_SETTINGS.VTEX.MODULES.UNAVAILABLE') }}
          </div>

          <div v-if="searchResults.length" class="flex flex-col gap-3">
            <div
              v-for="product in searchResults"
              :key="`${product.product_id}-${product.sku_id}`"
              class="rounded-xl border border-n-weak p-4 md:p-5"
            >
              <div class="flex flex-col md:flex-row gap-4">
                <div
                  class="h-24 w-24 rounded-lg border border-n-weak bg-n-alpha-black2 overflow-hidden shrink-0"
                >
                  <img
                    v-if="product.primary_image || product.image_url"
                    :src="product.primary_image || product.image_url"
                    :alt="product.name"
                    class="h-full w-full object-cover"
                  />
                  <div
                    v-else
                    class="h-full w-full flex items-center justify-center text-xs text-n-slate-10 px-2 text-center"
                  >
                    {{ $t('INTEGRATION_SETTINGS.VTEX.SEARCH.NO_IMAGE') }}
                  </div>
                </div>

                <div class="min-w-0 flex-1 flex flex-col gap-3">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <h4
                        class="text-sm md:text-base font-medium text-n-slate-12 break-words"
                      >
                        {{ product.name }}
                      </h4>
                      <p
                        v-if="
                          product.variant_name &&
                          product.variant_name !== product.name
                        "
                        class="text-sm text-n-slate-11 mt-1"
                      >
                        {{
                          formatLabelValue(
                            $t('INTEGRATION_SETTINGS.VTEX.SEARCH.VARIANT'),
                            product.variant_name
                          )
                        }}
                      </p>
                    </div>
                    <a
                      v-if="product.admin_url"
                      :href="product.admin_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-sm text-n-brand hover:underline shrink-0"
                    >
                      {{ $t('INTEGRATION_SETTINGS.VTEX.SEARCH.OPEN_ADMIN') }}
                    </a>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <span
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        formatLabelValue(
                          $t('INTEGRATION_SETTINGS.VTEX.SEARCH.SKU'),
                          product.sku_id
                        )
                      }}
                    </span>
                    <span
                      v-if="product.ref_id"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        formatLabelValue(
                          $t('INTEGRATION_SETTINGS.VTEX.SEARCH.REF_ID'),
                          product.ref_id
                        )
                      }}
                    </span>
                    <span
                      v-if="product.brand"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        formatLabelValue(
                          $t('INTEGRATION_SETTINGS.VTEX.SEARCH.BRAND'),
                          product.brand
                        )
                      }}
                    </span>
                    <span
                      v-if="product.color"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        formatLabelValue(
                          $t('INTEGRATION_SETTINGS.VTEX.SEARCH.COLOR'),
                          product.color
                        )
                      }}
                    </span>
                    <span
                      v-if="product.seller_name"
                      class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        formatLabelValue(
                          $t('INTEGRATION_SETTINGS.VTEX.SEARCH.SELLER'),
                          product.seller_name
                        )
                      }}
                    </span>
                    <span
                      class="rounded-full px-2.5 py-1 text-xs"
                      :class="
                        product.in_stock
                          ? 'bg-n-teal-2 text-n-teal-11'
                          : 'bg-n-ruby-2 text-n-ruby-11'
                      "
                    >
                      {{ stockLabel(product) }}
                    </span>
                  </div>

                  <p
                    v-if="product.variation_summary"
                    class="text-sm text-n-slate-10"
                  >
                    {{ product.variation_summary }}
                  </p>

                  <p
                    v-if="product.description"
                    class="text-sm text-n-slate-11 line-clamp-3"
                  >
                    {{ product.description }}
                  </p>

                  <div
                    v-if="productGallery(product).length > 1"
                    class="flex gap-2 overflow-x-auto pb-1"
                  >
                    <img
                      v-for="image in productGallery(product)"
                      :key="image"
                      :src="image"
                      :alt="product.name"
                      class="h-12 w-12 rounded-lg border border-n-weak object-cover shrink-0"
                    />
                  </div>

                  <div
                    v-if="productAttributes(product).length"
                    class="flex flex-wrap gap-2"
                  >
                    <span
                      v-for="attribute in productAttributes(product)"
                      :key="`${product.sku_id}-${attribute.name}`"
                      class="rounded-md border border-n-weak px-2.5 py-1 text-xs text-n-slate-11"
                    >
                      {{ formatLabelValue(attribute.name, attribute.value) }}
                    </span>
                  </div>

                  <div class="flex items-center justify-between gap-4">
                    <p class="text-sm md:text-base font-medium text-n-slate-12">
                      {{ formatProductPrice(product) }}
                    </p>
                    <span
                      v-if="product.is_variant"
                      class="text-xs text-n-slate-10"
                    >
                      {{
                        $t('INTEGRATION_SETTINGS.VTEX.SEARCH.VARIANT_PRODUCT')
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <Dialog
    ref="connectDialogRef"
    width="xl"
    :title="$t('INTEGRATION_SETTINGS.VTEX.HEADER')"
    :description="$t('INTEGRATION_SETTINGS.VTEX.CONNECTION.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onConnectDialogClose"
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="form.account_name"
          :label="$t('INTEGRATION_SETTINGS.VTEX.FORM.ACCOUNT_NAME')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.VTEX.FORM.ACCOUNT_NAME_PLACEHOLDER')
          "
        />
        <Input
          v-model="form.environment"
          :label="$t('INTEGRATION_SETTINGS.VTEX.FORM.ENVIRONMENT')"
          placeholder="vtexcommercestable"
        />
        <Input
          v-model="form.app_key"
          :label="$t('INTEGRATION_SETTINGS.VTEX.FORM.APP_KEY')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.VTEX.FORM.APP_KEY_PLACEHOLDER')
          "
        />
        <Input
          v-model="form.app_token"
          type="password"
          autocomplete="current-password"
          :label="$t('INTEGRATION_SETTINGS.VTEX.FORM.APP_TOKEN')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.VTEX.FORM.APP_TOKEN_PLACEHOLDER')
          "
        />
      </div>

      <p v-if="settings.connection?.app_key" class="text-sm text-n-slate-11">
        {{
          $t('INTEGRATION_SETTINGS.VTEX.CONNECTION.SAVED_KEY', {
            appKey: settings.connection.app_key,
          })
        }}
      </p>

      <div v-if="formError" class="text-sm text-n-ruby-10">
        {{ formError }}
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          faded
          slate
          type="button"
          :label="$t('DIALOG.BUTTONS.CANCEL')"
          @click="closeConnectDialog"
        />
        <Button
          blue
          type="button"
          :label="$t('INTEGRATION_SETTINGS.VTEX.CONNECTION.CONNECT')"
          :is-loading="uiFlags.isCreatingVtex"
          @click="connectVtex"
        />
      </div>
    </template>
  </Dialog>
</template>
